import {messages} from '@smartmixin/i18n';
import React from 'react';
import {
  DateTimeConfig,
  DAY,
  formatDate,
  formatDuration,
  getScaleStart,
  hourFormatter,
  shift,
} from '../Dates';
import {getPolarAngleFromDegrees} from '../Units';
import {
  MoonHorizonCrossing,
  MoonRegime,
  culmination,
  darkSkyWindow,
  getIllumination,
  getInfo,
  getNextPhaseTimestamp,
  getPosition,
  horizonCrossings,
  isAboveHorizon,
} from './astronomy';
import {EMoonPhase} from './phases';
import {getPhaseMessage} from './phaseName';
import {
  MoonIntl,
  formatIlluminationLine,
  formatMoonApparentSize,
  formatMoonCompass,
  formatMoonDegrees,
  formatMoonDistance,
} from './format';

/**
 * The moon in detail, for one place — reduced to the strings and decisions a
 * page renders.
 *
 * `nowView.ts` is the card; this is the screen behind it. Mobile ships
 * `screens/common/Moon/MoonDetailScreen/`; the webapp ships nothing behind its
 * card (FE #169). This is the extraction that has to land *before* that page is
 * written rather than after it: !483 built the webapp moon card by hand from
 * the mobile one, and the regime guard was then fixed on one side only, leaving
 * mobile captioning a moon 41° above the horizon "below the horizon all day"
 * for months (FE #171).
 *
 * Six sections, in the order the questions get asked: what is it doing
 * tonight, where to look for it, where is it right now, where are we in the
 * cycle, what the next seven nights hold, and whether tonight is any good for
 * observing. The last three of those — the dome, the week and the observing
 * section — landed after the first three (FE #129) and are additive: nothing
 * above them moved.
 *
 * What deliberately does NOT cross the seam, and must not start to:
 *
 * - **Icon names.** Mobile heads each section with a Phosphor glyph; core knows
 *   none of them, and neither section headings nor their order are decided here.
 * - **testIDs, styles, accessible-name composition.** Rows hand back `label`,
 *   `time` and `detail` as separate fields so each app composes its own.
 * - **Colours and layout.** The arc's element tree, its label clamping and its
 *   theme tints stay in each app; `altitudeArc.ts` returns geometry only.
 *
 * The two sampling instants are the load-bearing part and are the same ones the
 * card uses, for the same reasons documented there: the phase name and the
 * illumination come from **local midday**, because they are properties of the
 * day; the pass, the position and the cycle come from **`now`**, because rise,
 * culmination and the set that has already happened are relative to the moment
 * you are looking. Reading the fraction off the `now`-keyed object is what made
 * the card say 94.2 % while this screen said 94.8 % (FE #135); reading the pass
 * off the midday one showed a moonrise of 21:33 and a "+1 day" moonset at 05:00
 * while the panel below reported the moon 48° up.
 */

const SECONDS_PER_DAY = 86400;
const SECONDS_PER_MINUTE = 60;

/** Minutes of sky drawn either side of the pass, so it does not start on the edge. */
const ARC_MARGIN_MINUTES = 30;

/** Points across the arc. 6-minute resolution over a 14-hour pass. */
const ARC_SAMPLES = 150;

/** Matches `getLunarPhase`: a principal phase owns the half day either side. */
const PRINCIPAL_WINDOW_SECONDS = 12 * 3600;

/** Degrees of sky either side of the pass on the dome's azimuth axis. */
const DOME_PADDING_DEG = 8;
/** A compass point every 45° along that axis. */
const COMPASS_TICK_DEG = 45;

/** Rows in the week table. */
const NIGHTS = 7;
/**
 * How far ahead "the next properly dark night" looks, and what "properly"
 * means: two clear hours. A twenty-minute gap between moonset and dawn is a
 * dark-sky window by the card's definition and not a night worth planning for.
 */
const DARK_NIGHT_LOOKAHEAD_DAYS = 14;
const PROPERLY_DARK_SECONDS = 2 * 3600;
/** Coarser than the card's five minutes: only the length of each window matters. */
const LOOKAHEAD_STEP_SECONDS = 600;

const PRINCIPAL_PHASES = [
  EMoonPhase.New,
  EMoonPhase.QuarterMoon,
  EMoonPhase.Full,
  EMoonPhase.LastQuarter,
];

/**
 * Mean apparent diameter of the moon, in arcminutes, for the "vs average"
 * caption. Derived from the lunar radius and the mean distance rather than
 * pinned at 31.1, so it cannot drift from the diameter it is compared against.
 */
const MEAN_APPARENT_DIAMETER =
  (2 * Math.atan(1737.4 / 384400) * 180 * 60) / Math.PI;

export type MoonEventId = 'rise' | 'transit' | 'set' | 'noEvents';

export type MoonEventRow = {
  id: MoonEventId;
  /** "Moonrise", or "Above the horizon all day" on the collapsed row. */
  label: string;
  /**
   * "20:38", or `null` on a partial pass — `regime: 'normal'` with only one
   * horizon crossing in the window. The row degrades on its own rather than
   * collapsing the section.
   */
  time: string | null;
  /** "NE 47", "41° · S 180", "SW 251 · +1 day", or "" when unknown. */
  detail: string;
};

/**
 * Two shapes, not one with holes in it.
 *
 * Keyed on the regime ALONE, deliberately, and this is the same guard the card
 * carries. The obvious test — `regime !== 'normal' || rise === null || set ===
 * null` — is wrong, because the collapsed banner only distinguishes `alwaysUp`,
 * so every other case falls through to "below the horizon all day". A `normal`
 * regime can legitimately carry a null rise or a null set: the pass crosses the
 * horizon once in the window rather than twice. Sweeping real `getInfo` over
 * 2026 turns that up from **62°N** upward — Trondheim's latitude and
 * Reykjavik's, not the high Arctic — and at 63.0°N on 6 Sep 2026 10:00 UTC the
 * moon is 41.4° ABOVE the horizon with `rise === null` (FE #171).
 */
export type MoonTonight =
  | {
      kind: 'events';
      rows: [MoonEventRow, MoonEventRow, MoonEventRow];
      /** "Up for 8h43", or `null` when the pass has no rise/set pair to measure. */
      footer: string | null;
    }
  | {kind: 'noEvents'; row: MoonEventRow};

export type MoonMeasureId =
  | 'altitude'
  | 'direction'
  | 'distance'
  | 'apparentSize';

export type MoonMeasure = {
  id: MoonMeasureId;
  label: string;
  /** "41°", "S 180", "388.272 km", "30,08′". */
  value: string;
  /** "Above the horizon", "-1,0 % vs average", or "" where there is nothing to add. */
  detail: string;
};

export type MoonPhaseRow = {
  phase: EMoonPhase;
  /** "Last quarter". */
  label: string;
  /** "Fri 4 Sep 02:51". */
  time: string;
  /** "in 7 days", or "today". */
  relative: string;
};

export type MoonArcSample = {
  /** Minutes from the start of the window being drawn. */
  minute: number;
  /** Degrees above the horizon; negative below it. */
  altitude: number;
};

export type MoonDomeSample = MoonArcSample & {
  /**
   * Compass bearing, *unwrapped*: the first sample is the true bearing and
   * every later one is within a fraction of a turn of its predecessor, so a
   * moon that never sets sweeps past 360 rather than tearing the path at
   * north. `((azimuth % 360) + 360) % 360` is the bearing at that instant.
   */
  azimuth: number;
  /** Bright-limb angle from the zenith, in degrees, for the glyph on the path. */
  tilt: number;
};

/**
 * Where to look: tonight's path across the sky in real azimuth and altitude,
 * with a scrubber. The rows say the moon culminates at 08:44 at 69°; the dome
 * says which way to face, and the scrubber makes the bright-limb tilt legible
 * as it rotates across the night — the one thing the flat glyph convention
 * deliberately hides.
 */
export type MoonDome = {
  /** The same window as `arc.samples`, point for point. */
  samples: MoonDomeSample[];
  /** The azimuth axis, in the same unwrapped degrees, with a little air either side. */
  azimuthRange: {from: number; to: number};
  /** Every 45° point inside the range, labelled "NE", "E", … from the units messages. */
  compassTicks: {azimuth: number; label: string}[];
  /** The sample nearest to now, or `null` when now is outside the window. */
  nowIndex: number | null;
  /** "23:40 · 26° · SE 135°" for the scrubbed sample, built on demand. */
  describe: (index: number) => string;
  /** "Drag to step through the night". */
  hint: string;
};

/** One row of the next seven nights. */
export type MoonNight = {
  /** Local midnight starting the day, for keys. */
  day: number;
  /** "Fri 4". */
  label: string;
  today: boolean;
  /** At that day's midday, like the hero's — see `useMoonNowView`. */
  glyph: {fraction: number; waxing: boolean};
  /** "49 %". */
  lit: string;
  /** The first rise of the local day, or `null` on the day a month with none. */
  rise: string | null;
  /**
   * The set that follows that rise — not the one sharing its calendar day,
   * which belongs to the previous night's moon and would print a moon that
   * sets before it rises. `setNextDay` says when it falls on the next day.
   */
  set: string | null;
  setNextDay: boolean;
  /** "69°", the culmination of the pass printed; `null` when the moon never gets up. */
  maxAltitude: string | null;
};

/**
 * Tonight's verdict, promoted from the card's one-line pill, plus the next
 * night worth planning for.
 */
export type MoonObserving = {
  /** "Dark sky 22:35 → 23:45", or why there is none. */
  headline: string;
  /** "1h10 without the moon", or what is keeping the sky lit. */
  detail: string;
  /** "Next properly dark night" → "Sat 5 Sep · 6h20 moonless", or "None in the next two weeks". */
  next: {label: string; value: string};
};

export type MoonArc = {
  samples: MoonArcSample[];
  /** The above-horizon span, when the moon rises and sets inside the window. */
  upWindow: {from: number; to: number} | null;
  /** The culmination and its altitude, already formatted. `null` outside the window. */
  peak: {minute: number; label: string} | null;
  /** Where "now" falls in the window, or `null` when it is outside it. */
  nowMinute: number | null;
};

export type MoonDetailView = {
  regime: MoonRegime;
  /** Everything the disc needs; the SVG element tree stays in each app. */
  glyph: {fraction: number; waxing: boolean; tilt: number};
  /** "Waning gibbous". */
  phaseName: string;
  /** "94.2% lit · day 16.2 of 29.5". */
  illuminationLine: string;
  /** "Exact phase at 06:18" on a day that has one, `null` otherwise. */
  exactInstant: string | null;
  tonight: MoonTonight;
  arc: MoonArc;
  dome: MoonDome;
  measures: MoonMeasure[];
  /** The four principal phases, soonest first. */
  upcoming: MoonPhaseRow[];
  /** Seven local days, today first. */
  week: MoonNight[];
  observing: MoonObserving;
};

export type MoonDetailViewParams = {
  latitude: number;
  longitude: number;
  timezone: string;
  /** The instant the page is describing; both apps tick this once a minute. */
  now: number;
  intl: MoonIntl;
  dateTimeConfig: DateTimeConfig;
};

/**
 * The first night in the next fortnight with two clear hours, from local noon
 * of each day so "night" means the evening that follows it. Tonight is the
 * card's window and is described above it, so the search starts at
 * tomorrow's. `null` when the fortnight has none — Tromsø in early September.
 */
const findNextDarkNight = (
  startOfDay: number,
  timezone: string,
  latitude: number,
  longitude: number,
): {day: number; from: number; seconds: number} | null => {
  for (let day = 1; day <= DARK_NIGHT_LOOKAHEAD_DAYS; day += 1) {
    const noon = shift(startOfDay, timezone, day, 'days') + SECONDS_PER_DAY / 2;
    const dark = darkSkyWindow(
      noon,
      latitude,
      longitude,
      LOOKAHEAD_STEP_SECONDS,
    ).window;
    if (dark !== null && dark.to - dark.from >= PROPERLY_DARK_SECONDS) {
      return {day: noon, from: dark.from, seconds: dark.to - dark.from};
    }
  }
  return null;
};

/**
 * A hook rather than a plain builder, like `useMoonNowView` and
 * `Sun.useSunYearView`, because the memo boundaries are part of the contract:
 * the arc is 150 `getPosition` calls and is keyed on the *pass*, not on `now`,
 * so a once-a-minute tick moves the marker without resampling the sky.
 */
export const useMoonDetailView = ({
  latitude,
  longitude,
  timezone,
  now,
  intl,
  dateTimeConfig,
}: MoonDetailViewParams): MoonDetailView => {
  const {formatMessage} = intl;

  // The phase *name* is a property of the day, not of the minute: it is decided
  // by a 12-hour window around the exact instant, and local noon is the sample
  // that puts "Full moon" on the day the moon is actually full. Sampling it at
  // render time would caption a visibly full disc "Waning gibbous" all evening.
  const startOfDay = getScaleStart(now, timezone, DAY);
  const midday = startOfDay + SECONDS_PER_DAY / 2;

  const illumination = React.useMemo(() => getIllumination(midday), [midday]);
  const phase = illumination.phase;

  const info = React.useMemo(
    () => getInfo({timestamp: now, latitude, longitude}),
    [now, latitude, longitude],
  );
  const position = React.useMemo(
    () => getPosition({timestamp: now, latitude, longitude}),
    [now, latitude, longitude],
  );

  /** The next occurrence of each principal phase, soonest first. */
  const nextPhases = React.useMemo(
    () =>
      PRINCIPAL_PHASES.map((principal) => ({
        phase: principal,
        at: getNextPhaseTimestamp(new Date(now * 1000), principal),
      })).sort((a, b) => a.at - b.at),
    [now],
  );

  /**
   * The principal instant today, if there is one — "Full moon was today at
   * 06:18". Searched backwards from just after the window so a phase that has
   * already happened is still found.
   */
  const todaysPhaseInstant = React.useMemo(() => {
    if (!PRINCIPAL_PHASES.includes(phase)) {
      return null;
    }
    const from = new Date((midday - PRINCIPAL_WINDOW_SECONDS - 1) * 1000);
    const at = getNextPhaseTimestamp(from, phase);
    return Math.abs(at - midday) <= PRINCIPAL_WINDOW_SECONDS ? at : null;
  }, [phase, midday]);

  /**
   * The window the arc draws, and the altitudes across it.
   *
   * Keyed on the pass boundaries rather than on `info`, which is a fresh object
   * every minute now that it follows `now`: the window only moves when the pass
   * does, and resampling 150 positions a minute would be waste.
   *
   * Where there is no rise and no set — above about 61° that happens several
   * days a month — the window falls back to the whole local day, and the arc
   * still draws, because altitude is defined even when every event time is
   * null. Same argument the twilight band won.
   */
  const arcWindow = React.useMemo(() => {
    const hasPass =
      info.regime === 'normal' && info.rise !== null && info.set !== null;
    const from = hasPass
      ? (info.rise as number) - ARC_MARGIN_MINUTES * SECONDS_PER_MINUTE
      : startOfDay;
    const to = hasPass
      ? (info.set as number) + ARC_MARGIN_MINUTES * SECONDS_PER_MINUTE
      : startOfDay + SECONDS_PER_DAY;
    const step = (to - from) / ARC_SAMPLES;
    const minutesFrom = (timestamp: number) =>
      (timestamp - from) / SECONDS_PER_MINUTE;

    // One `getPosition` per sample serves both the arc (altitude) and the
    // dome (azimuth and tilt too). The azimuth is unwrapped as it goes: a
    // step of more than half a turn between neighbours is the path crossing
    // north, not the moon jumping across the sky.
    const dome: MoonDomeSample[] = [];
    for (let index = 0; index <= ARC_SAMPLES; index += 1) {
      const timestamp = from + index * step;
      const position = getPosition({timestamp, latitude, longitude});
      const previous = dome.length === 0 ? null : dome[dome.length - 1];
      let azimuth = position.azimuth;
      if (previous !== null) {
        const turns = Math.round((previous.azimuth - azimuth) / 360);
        azimuth += turns * 360;
      }
      dome.push({
        minute: minutesFrom(timestamp),
        altitude: position.altitude,
        azimuth,
        tilt: position.tilt,
      });
    }
    const azimuths = dome.map((sample) => sample.azimuth);

    return {
      from,
      to,
      samples: dome.map(({minute, altitude}) => ({minute, altitude})),
      dome,
      azimuthRange: {
        from: Math.min(...azimuths) - DOME_PADDING_DEG,
        to: Math.max(...azimuths) + DOME_PADDING_DEG,
      },
      upWindow: hasPass
        ? {
            from: minutesFrom(info.rise as number),
            to: minutesFrom(info.set as number),
          }
        : null,
      peakMinute:
        info.transit !== null && info.transit >= from && info.transit <= to
          ? minutesFrom(info.transit)
          : null,
    };
  }, [
    info.regime,
    info.rise,
    info.set,
    info.transit,
    startOfDay,
    latitude,
    longitude,
  ]);

  const hourFormat = hourFormatter(dateTimeConfig);
  const at = (timestamp: number | null) =>
    timestamp === null ? null : formatDate(timestamp, timezone, hourFormat);
  const onDay = (timestamp: number) =>
    formatDate(timestamp, timezone, 'ddd D MMM');

  const degrees = (value: number) => formatMoonDegrees(value, intl);
  const compass = (bearing: number | null) => formatMoonCompass(bearing, intl);

  /** Whether the set belongs to a later local day than the rise it follows. */
  const setsOnAnotherDay =
    info.rise !== null &&
    info.set !== null &&
    formatDate(info.rise, timezone, 'YYYY-MM-DD') !==
      formatDate(info.set, timezone, 'YYYY-MM-DD');

  const tonight = ((): MoonTonight => {
    if (info.regime !== 'normal') {
      return {
        kind: 'noEvents',
        row: {
          id: 'noEvents',
          label: formatMessage(
            info.regime === 'alwaysUp'
              ? messages.astral.aboveHorizonAllDay
              : messages.astral.belowHorizonAllDay,
          ),
          time: at(info.transit),
          // `transitAltitude` is null exactly when `transit` is, so the detail
          // empties with the value. Not a cosmetic guard:
          // `intl.formatNumber(null)` returns **"0"**, not "NaN", because
          // `Number(null) === 0` — dropping it prints a confident "0°".
          detail:
            info.transitAltitude === null ? '' : degrees(info.transitAltitude),
        },
      };
    }

    return {
      kind: 'events',
      rows: [
        {
          id: 'rise',
          label: formatMessage(messages.astral.moonrise),
          time: at(info.rise),
          detail: compass(info.riseAzimuth),
        },
        {
          id: 'transit',
          label: formatMessage(messages.astral.highestPoint),
          time: at(info.transit),
          detail:
            info.transitAltitude === null
              ? ''
              : `${degrees(info.transitAltitude)} · ${compass(
                  info.transitAzimuth,
                )}`,
        },
        {
          id: 'set',
          label: formatMessage(messages.astral.moonset),
          time: at(info.set),
          // "+1 day" only when it is one. The marker was unconditional, which
          // was harmless while the pass came from local midday — that pass
          // always sets after midnight — and wrong the moment the pass follows
          // `now`: at 05:00 the moon overhead sets at 11:02 the same morning.
          detail: `${compass(info.setAzimuth)}${
            setsOnAnotherDay
              ? ` · ${formatMessage(messages.astral.nextDay)}`
              : ''
          }`,
        },
      ],
      footer:
        info.rise === null || info.set === null
          ? null
          : formatMessage(messages.astral.upFor, {
              duration: formatDuration(info.set - info.rise, intl),
            }),
    };
  })();

  /**
   * The culmination the arc marks, memoised alongside `samples` and `upWindow`
   * rather than rebuilt each render: all three are handed to a chart, and a
   * fresh object identity once a second defeats the `React.memo` on it while
   * the pass it describes has not moved.
   */
  const peak = React.useMemo(
    () =>
      arcWindow.peakMinute === null || info.transitAltitude === null
        ? null
        : {
            minute: arcWindow.peakMinute,
            label: formatMoonDegrees(info.transitAltitude, intl),
          },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [arcWindow.peakMinute, info.transitAltitude, intl.formatNumber],
  );

  const compassTicks = React.useMemo(() => {
    const {from, to} = arcWindow.azimuthRange;
    const ticks: {azimuth: number; label: string}[] = [];
    for (
      let azimuth = Math.ceil(from / COMPASS_TICK_DEG) * COMPASS_TICK_DEG;
      azimuth <= to;
      azimuth += COMPASS_TICK_DEG
    ) {
      const point = getPolarAngleFromDegrees(((azimuth % 360) + 360) % 360);
      if (point !== null) {
        ticks.push({azimuth, label: formatMessage(messages.compass[point])});
      }
    }
    return ticks;
  }, [arcWindow.azimuthRange, formatMessage]);

  const nowMinute =
    now >= arcWindow.from && now <= arcWindow.to
      ? (now - arcWindow.from) / SECONDS_PER_MINUTE
      : null;
  const nowIndex =
    nowMinute === null
      ? null
      : arcWindow.dome.reduce(
          (closest, sample, index) =>
            Math.abs(sample.minute - nowMinute) <
            Math.abs(arcWindow.dome[closest].minute - nowMinute)
              ? index
              : closest,
          0,
        );

  /**
   * The next seven local days, from one scan of the whole span rather than
   * seven `getInfo`s — each of those rescans eight days and sweeps a night of
   * twilight, and none of it is wanted here.
   *
   * Keyed on the local day, not on `now`: nothing in a row moves within a
   * day. The numbers are memoised and the strings made in render, so the memo
   * depends on nothing that is rebuilt each render.
   */
  const nights = React.useMemo(() => {
    const weekEnd = shift(startOfDay, timezone, NIGHTS + 1, 'days');
    const crossings = horizonCrossings(
      startOfDay,
      weekEnd,
      latitude,
      longitude,
    );
    const first = (
      type: MoonHorizonCrossing['type'],
      after: number,
      before = Infinity,
    ): number | null =>
      crossings.find((c) => c.type === type && c.at >= after && c.at < before)
        ?.at ?? null;
    const peakOf = (from: number, to: number): number | null => {
      const peak = culmination(from, to, latitude, longitude);
      return peak.altitude > 0 ? peak.altitude : null;
    };

    return Array.from({length: NIGHTS}, (_, index) => {
      const dayStart = shift(startOfDay, timezone, index, 'days');
      const dayEnd = shift(startOfDay, timezone, index + 1, 'days');
      const rise = first('rise', dayStart, dayEnd);
      // The set that follows the rise, wherever it falls; with no rise, the
      // day's own set is the previous night's moon coming down and is the
      // right one to print.
      const set =
        rise === null ? first('set', dayStart, dayEnd) : first('set', rise);
      const peakAltitude = (() => {
        if (rise !== null && set !== null) {
          return peakOf(rise, set);
        }
        if (rise !== null) {
          return peakOf(rise, dayEnd);
        }
        if (set !== null) {
          return peakOf(dayStart, set);
        }
        return isAboveHorizon(dayStart, latitude, longitude)
          ? peakOf(dayStart, dayEnd)
          : null;
      })();
      const illumination = getIllumination(dayStart + SECONDS_PER_DAY / 2);

      return {
        dayStart,
        dayEnd,
        rise,
        set,
        peakAltitude,
        fraction: illumination.fraction,
        waxing: illumination.waxing,
      };
    });
  }, [startOfDay, timezone, latitude, longitude]);

  const week: MoonNight[] = nights.map((night, index) => ({
    day: night.dayStart,
    label: formatDate(night.dayStart, timezone, 'ddd D'),
    today: index === 0,
    glyph: {fraction: night.fraction, waxing: night.waxing},
    lit: intl.formatNumber(night.fraction, {
      style: 'percent',
      maximumFractionDigits: 0,
    }),
    rise: at(night.rise),
    set: at(night.set),
    setNextDay: night.set !== null && night.set >= night.dayEnd,
    maxAltitude:
      night.peakAltitude === null ? null : degrees(night.peakAltitude),
  }));

  const nextDarkNight = React.useMemo(
    () => findNextDarkNight(startOfDay, timezone, latitude, longitude),
    [startOfDay, timezone, latitude, longitude],
  );

  const observing = ((): MoonObserving => {
    const {darkWindow} = info;
    const next = {
      label: formatMessage(messages.astral.nextProperlyDarkNight),
      // The date the night is *named* by and the time the darkness actually
      // opens, which are often different days: a window at 00:40 belongs to
      // "Monday night" and starts on Tuesday. Printing the date alone invites
      // going out on Monday evening — 25 of 90 nights at this latitude.
      value:
        nextDarkNight === null
          ? formatMessage(messages.astral.noDarkNightAhead)
          : `${formatDate(nextDarkNight.day, timezone, 'ddd D MMM')} ${at(
              nextDarkNight.from,
            )} · ${formatMessage(messages.astral.moonlessFor, {
              duration: formatDuration(nextDarkNight.seconds, intl),
            })}`,
    };
    if (darkWindow !== null) {
      return {
        headline: formatMessage(messages.astral.darkSkyBetween, {
          from: at(darkWindow.from) as string,
          to: at(darkWindow.to) as string,
        }),
        detail: formatMessage(messages.astral.withoutTheMoon, {
          duration: formatDuration(darkWindow.to - darkWindow.from, intl),
        }),
        next,
      };
    }
    if (info.darkWindowBlockedBy === 'twilight') {
      return {
        headline: formatMessage(messages.astral.noAstronomicalDarknessTonight),
        detail: formatMessage(messages.astral.sunNeverDarkEnough),
        next,
      };
    }
    return {
      headline: formatMessage(messages.astral.brightAllNight),
      detail:
        info.rise !== null && info.set !== null
          ? formatMessage(messages.astral.moonWashesOutNight, {
              rise: at(info.rise) as string,
              set: at(info.set) as string,
            })
          : formatMessage(messages.astral.moonUpAllNight),
      next,
    };
  })();

  /**
   * The scrubber's readout, built per sample on demand — and memoised,
   * because it is a prop of a `React.memo`'d component that would otherwise
   * re-render on every one-minute tick for a sky that has not moved. The
   * whole dome is keyed on the pass, not on `now`.
   */
  const describe = React.useCallback(
    (index: number) => {
      const sample = arcWindow.dome[index];
      return formatMessage(messages.astral.domeReadout, {
        time: at(arcWindow.from + sample.minute * SECONDS_PER_MINUTE),
        altitude: formatMoonDegrees(sample.altitude, intl),
        direction: formatMoonCompass(
          ((sample.azimuth % 360) + 360) % 360,
          intl,
        ),
      });
    },
    // `at` closes over `timezone` and `hourFormat`, both listed; `intl` is
    // stable for a locale and the formatters are pure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [arcWindow.dome, arcWindow.from, timezone, hourFormat, intl, formatMessage],
  );

  const apparentDelta = position.apparentDiameter / MEAN_APPARENT_DIAMETER - 1;

  const measures: MoonMeasure[] = [
    {
      id: 'altitude',
      label: formatMessage(messages.astral.moonAltitude),
      value: degrees(position.altitude),
      // Only when it is. Leaving the caption unconditional captions a moon at
      // -12° "above the horizon". `info.isUp` rather than the sign of the
      // altitude: the rows above put the horizon where the crossings do.
      detail: info.isUp ? formatMessage(messages.astral.aboveTheHorizon) : '',
    },
    {
      id: 'direction',
      label: formatMessage(messages.astral.moonDirection),
      value: compass(position.azimuth),
      detail: '',
    },
    // Both through `format.ts`, shared with the card's third line: the card
    // and this page describe the same moon one tap apart (FE #134).
    {
      id: 'distance',
      label: formatMessage(messages.astral.moonDistance),
      value: formatMoonDistance(position.distance, intl),
      detail: '',
    },
    {
      id: 'apparentSize',
      label: formatMessage(messages.astral.apparentSize),
      value: formatMoonApparentSize(position.apparentDiameter, intl),
      detail: formatMessage(messages.astral.vsAverage, {
        percent: intl.formatNumber(apparentDelta, {
          style: 'percent',
          signDisplay: 'exceptZero',
          // Both bounds: without the minimum, Intl drops the trailing zero and
          // -0.998 % renders as "-1 %", which reads as a whole percent of
          // difference rather than a tenth.
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }),
      }),
    },
  ];

  return {
    regime: info.regime,
    glyph: {
      fraction: illumination.fraction,
      waxing: illumination.waxing,
      tilt: position.tilt,
    },
    phaseName: formatMessage(getPhaseMessage(phase)),
    illuminationLine: formatIlluminationLine(illumination, intl),
    exactInstant:
      todaysPhaseInstant === null
        ? null
        : formatMessage(messages.astral.exactPhaseAt, {
            time: at(todaysPhaseInstant) as string,
          }),
    tonight,
    arc: {
      samples: arcWindow.samples,
      upWindow: arcWindow.upWindow,
      peak,
      nowMinute,
    },
    dome: {
      samples: arcWindow.dome,
      azimuthRange: arcWindow.azimuthRange,
      compassTicks,
      nowIndex,
      describe,
      hint: formatMessage(messages.astral.domeHint),
    },
    measures,
    week,
    observing,
    upcoming: nextPhases.map(({phase: principal, at: when}) => ({
      phase: principal,
      label: formatMessage(getPhaseMessage(principal)),
      time: `${onDay(when)} ${at(when)}`,
      // Rounding alone yields "in 0 days" for anything under twelve hours out,
      // which is most of the day a phase happens on.
      relative: (() => {
        const days = Math.round((when - now) / SECONDS_PER_DAY);
        return days === 0
          ? formatMessage(messages.calendar.today)
          : formatMessage(messages.astral.inDays, {days});
      })(),
    })),
  };
};
